/*
 * File: AssignedRule.tsx
 * Project: beaker
 * -----
 * Last Modified: 27 Apr 2022 11:11:11 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Collapse, Dropdown, List, Menu, message, Row } from 'antd';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { AssignRulesForm } from 'components/molecules/AssignRulesForm';
import { Stack } from 'components/UI';
import { AntPageTitle } from 'components/UI/AntPageTitle';
import { FormikProvider, useFormik } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { ProductCatalogItemPayload, productCatalogItemPayloadSchema } from 'models/ProductCatalog';
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetProductCatalogItemQuery, useUpdateProductCatalogItemMutation } from 'redux/services/julia/juliaApi';
import { useGetCatalogConfigQuery } from 'redux/services/rudyCadabby/rudyCadabbyApi';

const styles: InlineStylesModel = {
  //
};

export const AssignedRule: React.FC = () => {
  /* ******************** Hooks ******************** */
  const navigate = useNavigate();
  const { id: entityId, productId } = useParams();
  const { pathname } = useLocation();
  const isBusinessEdit = pathname.includes('business/');

  const { data: itemResponse, isLoading: isItemLoading } = useGetProductCatalogItemQuery({ customerId: entityId || '', productId: productId || '' }, { skip: !productId });

  const { data: catalogConfig } = useGetCatalogConfigQuery({ customerId: entityId as string }, { skip: !entityId });

  const data = itemResponse;
  const isLoading = isItemLoading;
  const isBusinessOnly = catalogConfig?.dataInheritanceLevel === 'Business';
  const { Panel } = Collapse;

  const [updateBusinessItem] = useUpdateProductCatalogItemMutation();

  const formik = useFormik<ProductCatalogItemPayload>({
    enableReinitialize: true,
    validationSchema: productCatalogItemPayloadSchema,
    initialValues: {
      defaultConfigType: data ? data.defaultConfigType : '',
      defaultOverageOwner: data ? data.defaultOverageOwner : '',
      deploymentType: data ? data.deploymentType : '',
      isCommonStock: data ? data.isCommonStock : true,
      defaultOverageCondition: data ? data.defaultOverageCondition : '',
      defaultOverageDisposition: data ? data.defaultOverageDisposition : '',
      productNumber: data ? data.productNumber : '',
      productName: data ? data.productName || '' : '',
      category: data ? data.category || '' : '',
      manufacturer: data ? data.manufacturer || '' : '',
      isOverridden: data ? data.isOverridden : catalogConfig?.dataInheritanceLevel !== 'Business' ? true : false,
      isActive: data?.isActive ? data.isActive : true,
      customerXRef: {
        alternateItemId: data?.customerXRef?.alternateItemId ?? '',
        alternateItemIdLabel: data?.customerXRef?.alternateItemIdLabel ?? '',
        customerStandardCost: data?.customerXRef ? (data?.customerXRef.customerStandardCost === 0 ? '0' : data?.customerXRef?.customerStandardCost) : 0,
        modifiedDateTime: data?.customerXRef?.modifiedDateTime ?? ''
      }
    },
    onSubmit: async (values) => {
      if (!entityId || !productId || !data) {
        message.error("Entity or product doesn't exits");

        return;
      }
      try {
        const updateData = await updateBusinessItem({
          customerId: entityId,
          productId: productId,
          payload: { ...data, ...{ ...values, customerXRef: { customerStandardCost: values.customerXRef?.customerStandardCost as number } } }
        }).unwrap();

        message.success(`Business catalog item ${updateData.productNumber} was successfully updated!`);
      } catch (error) {
        message.error('Business catalog item failed to update!');
      }
    }
  });

  /* ******************** Functions ******************** */

  const handleClose = (): void => {
    if (isBusinessEdit) {
      navigate(`/business/${entityId}`);
    } else {
      navigate(`/division/${entityId}`);
    }
  };

  const handleSave = async (close?: boolean): Promise<void> => {
    await formik.submitForm();
    if (close) {
      handleClose();
    }
  };

  const menuJSX = (
    <Menu disabledOverflow={true}>
      <Menu.Item key="1" onClick={(): Promise<void> => handleSave(false)}>
        Save
      </Menu.Item>
    </Menu>
  );

  if (isLoading) {
    return (
      <Row justify="center" align="middle">
        <Col span={24}>
          <BlockLoader direction="loader loader--slideUp" />
        </Col>
      </Row>
    );
  }

  return (
    <Stack flexDirection="column">
      <Col style={{ padding: 10 }}>
        <Stack justifyContent="space-between">
          <AntPageTitle text={`Edit: ${data?.productNumber ?? ''} ${isBusinessOnly && !isBusinessEdit ? ': : READ ONLY' : ''}`} />
          <Stack>
            <Button onClick={handleClose} icon={<ArrowLeftOutlined />}>
              Back
            </Button>
            <Dropdown.Button overlay={menuJSX} onClick={(): Promise<void> => handleSave(true)} loading={formik.isSubmitting} type="primary" icon={<SaveOutlined />}>
              Save and Exit
            </Dropdown.Button>
          </Stack>
        </Stack>
      </Col>

      <FormikProvider value={formik}>
        <AssignRulesForm isInheritanceBusiness={isBusinessOnly && !isBusinessEdit} />
      </FormikProvider>
      <Collapse>
        <Panel style={{ background: 'rgb(255, 234, 193)' }} key="1" header="Audit History">
          <List dataSource={itemResponse?.auditMessages} renderItem={(item): JSX.Element => <List.Item>{item}</List.Item>} />
        </Panel>
      </Collapse>
    </Stack>
  );
};
